import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon, ViewIcon } from "@chakra-ui/icons";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { db } from "../../configs/firebase";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { usePagination } from "../../store/hooks/usePagination";
import AppPagination from "../Pagination/AppPagination";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

type ServiceType =
  | "product"
  | "delivery"
  | "accommodation"
  | "car_hire"
  | "trip";

type StatusOption = {
  value: string;
  label: string;
  description?: string;
  terminal?: boolean;
};

type StatusOptionsResponse = {
  orderId?: string;
  orderType?: ServiceType;
  currentStatus?: string;
  currentStatusLabel?: string;
  managementScope?: "all_statuses" | "allowed_transitions" | string;
  statuses?: string[];
  statusOptions?: StatusOption[];
};

const SERVICE_STATUS_TRANSITIONS: Record<
  ServiceType,
  Record<string, string[]>
> = {
  product: {
    pending: ["confirmed", "cancelled"],
    confirmed: ["processing", "ready_for_collection", "assigned", "cancelled"],
    processing: ["ready_for_collection", "assigned", "cancelled"],
    ready_for_collection: ["completed", "assigned", "cancelled"],
    assigned: ["awaiting_pickup", "arrived_at_pickup", "cancelled"],
    awaiting_pickup: ["arrived_at_pickup", "picked_up", "cancelled"],
    arrived_at_pickup: ["picked_up", "cancelled"],
    picked_up: ["shipped", "in_transit", "return_in_transit"],
    shipped: ["in_transit", "out_for_delivery", "delivered"],
    in_transit: [
      "out_for_delivery",
      "arrived_at_delivery",
      "delivery_failed_customer_unavailable",
    ],
    out_for_delivery: [
      "arrived_at_delivery",
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    arrived_at_delivery: [
      "proof_of_delivery_pending",
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    proof_of_delivery_pending: [
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    delivery_failed_customer_unavailable: [
      "arrived_at_delivery",
      "return_in_transit",
      "cancelled",
    ],
    return_in_transit: ["returned"],
    returned: [],
    delivered: ["completed", "returned"],
    completed: [],
    cancelled: [],
  },
  delivery: {
    pending: ["assigned", "cancelled"],
    assigned: ["awaiting_pickup", "arrived_at_pickup", "cancelled"],
    awaiting_pickup: ["arrived_at_pickup", "picked_up", "cancelled"],
    arrived_at_pickup: ["picked_up", "cancelled"],
    picked_up: ["in_transit", "return_in_transit"],
    in_transit: [
      "out_for_delivery",
      "arrived_at_delivery",
      "delivery_failed_customer_unavailable",
    ],
    out_for_delivery: [
      "arrived_at_delivery",
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    arrived_at_delivery: [
      "proof_of_delivery_pending",
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    proof_of_delivery_pending: [
      "delivered",
      "delivery_failed_customer_unavailable",
    ],
    delivery_failed_customer_unavailable: [
      "arrived_at_delivery",
      "return_in_transit",
      "cancelled",
    ],
    return_in_transit: ["returned_to_sender"],
    returned_to_sender: ["completed"],
    delivered: ["completed"],
    completed: [],
    cancelled: [],
  },
  accommodation: {
    pending: ["confirmed", "cancelled"],
    confirmed: ["ready_for_check_in", "checked_in", "no_show", "cancelled"],
    ready_for_check_in: ["checked_in", "no_show", "cancelled"],
    checked_in: ["in_stay", "checked_out"],
    in_stay: ["checked_out"],
    checked_out: ["completed"],
    completed: [],
    no_show: [],
    cancelled: [],
  },
  car_hire: {
    pending: ["confirmed", "cancelled"],
    confirmed: [
      "ready_for_pickup",
      "vehicle_collected",
      "no_show",
      "cancelled",
    ],
    ready_for_pickup: ["vehicle_collected", "no_show", "cancelled"],
    vehicle_collected: ["in_progress", "return_due", "vehicle_returned"],
    in_progress: ["return_due", "vehicle_returned"],
    return_due: ["vehicle_returned"],
    vehicle_returned: ["inspection_pending", "completed"],
    inspection_pending: ["completed"],
    completed: [],
    no_show: [],
    cancelled: [],
  },
  trip: {
    pending: ["confirmed", "cancelled"],
    confirmed: ["checked_in", "onboarded", "no_show", "cancelled"],
    checked_in: ["onboarded", "no_show", "cancelled"],
    onboarded: ["in_progress", "completed"],
    in_progress: ["completed"],
    completed: [],
    no_show: [],
    cancelled: [],
  },
};

const SERVICE_PROGRESS_STEPS: Record<ServiceType, string[]> = {
  product: [
    "pending",
    "confirmed",
    "processing",
    "ready_for_collection",
    "assigned",
    "awaiting_pickup",
    "arrived_at_pickup",
    "picked_up",
    "shipped",
    "in_transit",
    "out_for_delivery",
    "arrived_at_delivery",
    "proof_of_delivery_pending",
    "delivered",
    "completed",
  ],
  delivery: [
    "pending",
    "assigned",
    "awaiting_pickup",
    "arrived_at_pickup",
    "picked_up",
    "in_transit",
    "out_for_delivery",
    "arrived_at_delivery",
    "proof_of_delivery_pending",
    "delivered",
    "completed",
  ],
  accommodation: [
    "pending",
    "confirmed",
    "ready_for_check_in",
    "checked_in",
    "in_stay",
    "checked_out",
    "completed",
  ],
  car_hire: [
    "pending",
    "confirmed",
    "ready_for_pickup",
    "vehicle_collected",
    "in_progress",
    "return_due",
    "vehicle_returned",
    "inspection_pending",
    "completed",
  ],
  trip: [
    "pending",
    "confirmed",
    "checked_in",
    "onboarded",
    "in_progress",
    "completed",
  ],
};

const EXCEPTION_STATUSES = new Set([
  "cancelled",
  "no_show",
  "delivery_failed_customer_unavailable",
  "return_in_transit",
  "returned",
  "returned_to_sender",
]);

const ALL_SERVICE_STATUSES = Array.from(
  new Set(
    Object.values(SERVICE_STATUS_TRANSITIONS).flatMap((transitions) =>
      Object.keys(transitions),
    ),
  ),
);

const ORDER_FILTER_STATUSES = ["draft", "paid", ...ALL_SERVICE_STATUSES];

const PAYMENT_FILTER_STATUSES = [
  "pending",
  "paid",
  "cash_expected",
  "cash_collected",
  "failed",
  "cancelled",
  "partially_paid",
];

const WAYBILL_STATUSES = [
  "pending_payment",
  "pending_dispatch",
  "notified",
  "claimed",
  "assigned",
  "driver_assigned",
  "arrived_at_pickup",
  "picked_up",
  "in_transit",
  "arrived_at_delivery",
  "proof_of_delivery_pending",
  "delivery_failed_customer_unavailable",
  "delivery_failed",
  "return_in_transit",
  "return_started",
  "returned_to_sender",
  "returned",
  "delivered",
  "cancelled",
  "expired",
];

const PAID_PAYMENT_STATUSES = ["paid", "cash_collected"];

type OrderItem = {
  productId?: string;
  _id?: string;
  name?: string;
  title?: string;
  category?: string;
  serviceCategory?: string;
  orderType?: string;
  type?: string;
  itemType?: string;
  serviceType?: string;
  module?: string;
  meta?: {
    category?: string;
    serviceCategory?: string;
    orderType?: string;
    [key: string]: any;
  };
  quantity?: number;
  price?: number;
  sellerUserId?: string;
  ownerId?: string;
  userId?: string;
};

type OrderPayment = {
  method?: string;
  provider?: string;
  status?: string;
};

type OrderLocation = {
  lat?: number;
  lng?: number;
};

type Order = {
  _id: string;
  email?: string;
  name?: string;
  phoneNumber?: string;
  userId?: string;
  ownerUserId?: string;
  sellerUserId?: string;
  sellerName?: string;
  checkoutGroupId?: string;
  checkoutGroupOrderCount?: number;
  fulfilmentOrderNumber?: string;
  total?: number;
  currency?: string;
  status?: string;
  category?: string;
  serviceCategory?: string;
  orderType?: string;
  type?: string;
  serviceType?: string;
  module?: string;
  metadata?: {
    category?: string;
    serviceCategory?: string;
    orderType?: string;
    [key: string]: any;
  };
  checkoutMode?: string;
  createdAt?: string;
  updatedAt?: string;
  fromAddress?: string;
  toAddress?: string;
  fromLocation?: OrderLocation;
  toLocation?: OrderLocation;
  payment?: OrderPayment;
  items?: OrderItem[];
  pricingBreakdown?: {
    productSubtotal?: number;
    serviceFee?: number;
    deliveryFee?: number;
    gatewayFee?: number;
    total?: number;
    driverEarningTotal?: number;
    deliveryMargin?: number;
    platformNetRevenue?: number;
    sellerCommissionTotal?: number;
    sellerNetPayableTotal?: number;
  };
  deliveryTracking?: {
    waybillId?: string;
    waybillNumber?: string;
    routeBatchId?: string;
    dispatchJobId?: string;
    status?: string;
    assignedDriverId?: string;
    assignedDriverName?: string;
    assignedDriverPhone?: string;
    assignedDriverVehicleType?: string;
    assignedAt?: string;
    arrivedAtPickupAt?: string;
    pickedUpAt?: string;
    inTransitAt?: string;
    arrivedAtDeliveryAt?: string;
    proofOfDeliveryPendingAt?: string;
    deliveredAt?: string;
    returnedAt?: string;
  };
};

type Waybill = {
  _id: string;
  waybillNumber?: string;
  checkoutGroupId?: string;
  orderId?: string;
  buyerUserId?: string;
  sellerUserId?: string;
  sellerName?: string;
  packageIndex?: number;
  packageCount?: number;
  pickupWarehouseId?: string;
  pickupWarehouseName?: string;
  status?: string;
  driverId?: string;
  driverName?: string;
  routeBatchId?: string;
  routeSequence?: number;
  createdAt?: string;
  updatedAt?: string;
};

type DispatchJob = {
  _id: string;
  sourceType?: "order" | "delivery";
  sourceId?: string;
  ownerUserId?: string;
  warehouseId?: string;
  warehouseName?: string;
  pickupAddress?: string;
  dropoffAddress?: string;
  assignedDriverId?: string;
  assignedDriverName?: string;
  assignedDriverPhone?: string;
  assignedDriverVehicleType?: string;
  candidateDriverIds?: string[];
  candidateDriverDetails?: Array<{
    rank?: number;
    driverId?: string;
    name?: string;
    vehicleType?: string;
    ratingScore?: number;
    currentJobsCount?: number;
    maxConcurrentJobs?: number;
    distanceToPickupKm?: number | null;
    estimatedPickupMinutes?: number | null;
    locationFresh?: boolean;
    locationAgeSeconds?: number | null;
    candidateSource?: "warehouse" | "nearby" | "warehouse_nearby" | string;
  }>;
  status?: string;
  driverEarning?: number;
  deliveryFee?: number;
  orderValue?: number;
  createdAt?: string;
  updatedAt?: string;
};

type DriverRow = {
  id: string;
  name?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  vehicleType?: string;
  status?: string;
  currentShiftStatus?: string;
  currentJobsCount?: number;
  maxConcurrentJobs?: number;
  capacityKg?: number;
  serviceCategories?: string[];
  ratingScore?: number;
  createdAt?: string;
  updatedAt?: string;
};

type CheckoutGroupRow = {
  key: string;
  primaryOrder: Order;
  orders: Order[];
  waybills: Waybill[];
  dispatchJobs: DispatchJob[];
  serviceType: ServiceType;
  email: string;
  customerName: string;
  customerPhone: string;
  buyerUserId: string;
  checkoutGroupId: string;
  orderCount: number;
  sellerCount: number;
  sellerNames: string[];
  total: number;
  status: string;
  paymentStatus: string;
  dispatchStatus: string;
  hasDeliveryWork: boolean;
  hasDispatchJob: boolean;
  hasAssignedDriver: boolean;
  topCategory: string;
  totalItems: number;
  createdAt?: string;
  updatedAt?: string;
};

const getCheckoutKey = (order: Order) =>
  String(order.checkoutGroupId || order._id || "").trim();

const shortenId = (value?: string, start = 7, end = 5) => {
  const clean = String(value || "").trim();
  if (!clean) return "—";
  if (clean.length <= start + end + 3) return clean;
  return `${clean.slice(0, start)}...${clean.slice(-end)}`;
};

const getOrderItemName = (item: OrderItem) =>
  String(item.title || item.name || item.productId || item._id || "Item");

const formatMoney = (value?: number, currency = "MWK") =>
  `${currency} ${Number(value || 0).toLocaleString()}`;

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const humanize = (value?: string) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "Unknown";

const normalizeStatusKey = (value?: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");

const normalizeServiceTypeValue = (value?: unknown): ServiceType | null => {
  const normalized = normalizeStatusKey(value);

  if (
    ["product", "products", "product_order", "marketplace"].includes(normalized)
  ) {
    return "product";
  }

  if (
    ["delivery", "courier", "direct_delivery", "a_to_b"].includes(normalized)
  ) {
    return "delivery";
  }

  if (
    ["accommodation", "accommodations", "hotel", "room", "lodge"].includes(
      normalized,
    )
  ) {
    return "accommodation";
  }

  if (
    ["car_hire", "carhire", "car_hiring", "vehicle_hire"].includes(normalized)
  ) {
    return "car_hire";
  }

  if (["trip", "trips", "hike", "travel", "tour"].includes(normalized)) {
    return "trip";
  }

  return null;
};

const resolveOrderServiceType = (order?: Order | null): ServiceType => {
  const candidates = [
    order?.serviceCategory,
    order?.category,
    order?.orderType,
    order?.type,
    order?.serviceType,
    order?.module,
    order?.metadata?.serviceCategory,
    order?.metadata?.category,
    order?.metadata?.orderType,
    ...(order?.items || []).flatMap((item) => [
      item.serviceCategory,
      item.category,
      item.orderType,
      item.type,
      item.itemType,
      item.serviceType,
      item.module,
      item.meta?.serviceCategory,
      item.meta?.category,
      item.meta?.orderType,
    ]),
  ];

  for (const candidate of candidates) {
    const resolved = normalizeServiceTypeValue(candidate);
    if (resolved) return resolved;
  }

  return "product";
};

const getGroupServiceType = (orders: Order[]): ServiceType => {
  const types = Array.from(new Set(orders.map(resolveOrderServiceType)));

  if (types.length === 1) return types[0];
  if (types.includes("delivery")) return "delivery";

  return resolveOrderServiceType(orders[0]);
};

const normalizeOrderProgressStatus = (
  status?: string,
  serviceType: ServiceType = "product",
) => {
  const clean = normalizeStatusKey(status || "pending");

  if (clean === "draft") return "pending";
  if (clean === "paid")
    return serviceType === "delivery" ? "pending" : "confirmed";

  const commonAliases: Record<string, string> = {
    driver_assigned: "assigned",
    claimed: "assigned",
    ready: serviceType === "product" ? "ready_for_collection" : clean,
    delivery_failed: "delivery_failed_customer_unavailable",
    return_started: "return_in_transit",
    checkedin: "checked_in",
    checkedout: "checked_out",
    check_in: "checked_in",
    check_out: "checked_out",
    boarded: "onboarded",
    onboard: "onboarded",
    vehicle_picked_up: "vehicle_collected",
    vehicle_collected_by_customer: "vehicle_collected",
  };

  const normalized = commonAliases[clean] || clean || "pending";

  if (serviceType === "delivery" && normalized === "returned") {
    return "returned_to_sender";
  }

  return normalized;
};

const getStatusColorScheme = (status?: string) => {
  const normalized = normalizeStatusKey(status);

  if (
    ["completed", "delivered", "checked_out", "vehicle_returned"].includes(
      normalized,
    )
  ) {
    return "green";
  }

  if (
    [
      "confirmed",
      "ready_for_collection",
      "ready_for_check_in",
      "ready_for_pickup",
      "checked_in",
      "onboarded",
    ].includes(normalized)
  ) {
    return "blue";
  }

  if (
    [
      "assigned",
      "awaiting_pickup",
      "arrived_at_pickup",
      "vehicle_collected",
    ].includes(normalized)
  ) {
    return "cyan";
  }

  if (
    [
      "processing",
      "picked_up",
      "shipped",
      "in_transit",
      "out_for_delivery",
      "arrived_at_delivery",
      "proof_of_delivery_pending",
      "in_stay",
      "in_progress",
      "return_due",
      "inspection_pending",
    ].includes(normalized)
  ) {
    return "purple";
  }

  if (
    [
      "returned",
      "returned_to_sender",
      "return_in_transit",
      "delivery_failed_customer_unavailable",
      "no_show",
    ].includes(normalized)
  ) {
    return "orange";
  }

  if (normalized === "cancelled") return "red";
  if (normalized === "pending") return "yellow";

  return "gray";
};

const getPaymentStatusColorScheme = (status?: string) => {
  if (status === "paid") return "green";
  if (status === "cash_collected") return "teal";
  if (status === "cash_expected") return "orange";
  if (status === "failed") return "red";
  if (status === "cancelled") return "red";
  if (status === "pending") return "yellow";
  if (status === "partially_paid") return "purple";
  return "gray";
};

const getDispatchColorScheme = (status?: string, hasDeliveryWork?: boolean) => {
  const normalized = normalizeStatusKey(status);

  if (!hasDeliveryWork) return "gray";
  if (["delivered", "returned_to_sender"].includes(normalized)) return "green";
  if (
    [
      "assigned",
      "driver_assigned",
      "arrived_at_pickup",
      "picked_up",
      "in_transit",
      "arrived_at_delivery",
      "proof_of_delivery_pending",
    ].includes(normalized)
  ) {
    return "cyan";
  }
  if (
    ["notified", "claimed", "pending_driver", "pending_dispatch"].includes(
      normalized,
    )
  ) {
    return "orange";
  }
  if (
    [
      "delivery_failed_customer_unavailable",
      "return_in_transit",
      "cancelled",
      "expired",
      "not_dispatched",
    ].includes(normalized)
  ) {
    return "red";
  }
  return "gray";
};

const getGroupStatus = (orders: Order[]) => {
  const serviceType = getGroupServiceType(orders);
  const statuses = orders.map((order) =>
    normalizeOrderProgressStatus(
      order.status || "pending",
      resolveOrderServiceType(order),
    ),
  );

  if (!statuses.length) return "pending";
  if (statuses.every((status) => status === statuses[0])) return statuses[0];

  const exceptionPriority = [
    "cancelled",
    "no_show",
    "delivery_failed_customer_unavailable",
    "return_in_transit",
    "returned",
    "returned_to_sender",
  ];

  for (const exceptionStatus of exceptionPriority) {
    if (statuses.includes(exceptionStatus)) return exceptionStatus;
  }

  const steps = SERVICE_PROGRESS_STEPS[serviceType];
  const ranked = statuses
    .map((status) => ({
      status,
      index: steps.indexOf(status),
    }))
    .filter((item) => item.index >= 0)
    .sort((a, b) => a.index - b.index);

  return ranked[0]?.status || statuses[0] || "pending";
};

const getGroupPaymentStatus = (orders: Order[]) => {
  const paymentStatuses = orders.map((order) =>
    String(order.payment?.status || "pending")
      .trim()
      .toLowerCase(),
  );

  if (paymentStatuses.every((status) => status === "paid")) return "paid";
  if (paymentStatuses.every((status) => status === "cash_collected")) {
    return "cash_collected";
  }
  if (paymentStatuses.some((status) => status === "paid")) {
    return "partially_paid";
  }
  if (paymentStatuses.some((status) => status === "cash_collected")) {
    return "cash_collected";
  }
  if (paymentStatuses.some((status) => status === "cash_expected")) {
    return "cash_expected";
  }
  if (paymentStatuses.some((status) => status === "failed")) return "failed";

  return paymentStatuses.find(Boolean) || "pending";
};

const isPaymentConfirmed = (paymentStatus?: string) =>
  PAID_PAYMENT_STATUSES.includes(String(paymentStatus || "").toLowerCase());

const getTopCategory = (orders: Order[]) => getGroupServiceType(orders);

const getCreatedDate = (orders: Order[]) => {
  const timestamps = orders
    .map((order) => (order.createdAt ? new Date(order.createdAt).getTime() : 0))
    .filter(Boolean);

  if (!timestamps.length) return undefined;

  return new Date(Math.min(...timestamps)).toISOString();
};

const isDeliveryOrder = (order: Order) => {
  const serviceType = resolveOrderServiceType(order);
  const checkoutMode = normalizeStatusKey(order.checkoutMode);
  const tracking = order.deliveryTracking || {};

  if (serviceType === "delivery") return true;

  if (serviceType !== "product") return false;

  return Boolean(
    checkoutMode === "delivery" ||
    tracking.dispatchJobId ||
    tracking.routeBatchId ||
    tracking.waybillId,
  );
};

const hasDispatchJob = (
  orders: Order[],
  waybills: Waybill[],
  jobs: DispatchJob[] = [],
) => {
  return (
    jobs.length > 0 ||
    orders.some((order) => {
      const tracking = order.deliveryTracking || {};
      return Boolean(
        tracking.dispatchJobId || tracking.routeBatchId || tracking.waybillId,
      );
    }) ||
    waybills.some((waybill) =>
      Boolean(waybill.routeBatchId || waybill.waybillNumber),
    )
  );
};

const hasAssignedDriver = (
  orders: Order[],
  waybills: Waybill[],
  jobs: DispatchJob[] = [],
) => {
  return (
    jobs.some((job) => Boolean(job.assignedDriverId)) ||
    orders.some((order) => Boolean(order.deliveryTracking?.assignedDriverId)) ||
    waybills.some((waybill) => Boolean(waybill.driverId))
  );
};

const getDispatchStatus = (
  orders: Order[],
  waybills: Waybill[],
  jobs: DispatchJob[] = [],
) => {
  const jobStatuses = jobs
    .map((job) => normalizeStatusKey(job.status))
    .filter(Boolean);

  const trackingStatuses = orders
    .map((order) => normalizeStatusKey(order.deliveryTracking?.status))
    .filter(Boolean);

  const waybillStatuses = waybills
    .map((waybill) => normalizeStatusKey(waybill.status))
    .filter(Boolean);

  const statuses = [...jobStatuses, ...trackingStatuses, ...waybillStatuses];

  const priority = [
    "delivery_failed_customer_unavailable",
    "return_in_transit",
    "returned_to_sender",
    "cancelled",
    "expired",
    "delivered",
    "proof_of_delivery_pending",
    "arrived_at_delivery",
    "in_transit",
    "picked_up",
    "arrived_at_pickup",
    "assigned",
    "claimed",
    "notified",
    "pending_dispatch",
  ];

  for (const status of priority) {
    if (statuses.includes(status)) return status;
  }

  if (hasAssignedDriver(orders, waybills, jobs)) return "assigned";
  if (hasDispatchJob(orders, waybills, jobs)) return "pending_driver";

  return "not_dispatched";
};

const canMarkDelivered = (row: CheckoutGroupRow) => {
  if (!row.hasDeliveryWork) return true;
  return row.hasDispatchJob && row.hasAssignedDriver;
};

const getBlockedStatusReason = (row: CheckoutGroupRow, nextStatus: string) => {
  const serviceType = row.serviceType;
  const currentStatus = normalizeOrderProgressStatus(
    row.primaryOrder.status || "pending",
    serviceType,
  );
  const normalizedNextStatus = normalizeOrderProgressStatus(
    nextStatus,
    serviceType,
  );

  if (normalizedNextStatus === currentStatus) return "";

  if (normalizedNextStatus === "delivered" && !canMarkDelivered(row)) {
    return "Assign a driver or create dispatch first before marking this delivery as delivered.";
  }

  return "";
};

const getOrderProgress = (serviceType: ServiceType, status?: string) => {
  const normalized = normalizeOrderProgressStatus(status, serviceType);
  const steps = SERVICE_PROGRESS_STEPS[serviceType];
  const index = steps.indexOf(normalized);

  return {
    current: index >= 0 ? index + 1 : 0,
    total: steps.length,
    isException: EXCEPTION_STATUSES.has(normalized),
  };
};

const getFallbackStatusOptions = ({
  order,
  role,
}: {
  order: Order;
  role: string;
}): StatusOption[] => {
  const serviceType = resolveOrderServiceType(order);
  const currentStatus = normalizeOrderProgressStatus(order.status, serviceType);
  const normalizedRole = normalizeStatusKey(role);
  const allStatuses = Object.keys(SERVICE_STATUS_TRANSITIONS[serviceType]);
  const statuses = ["admin", "superadmin"].includes(normalizedRole)
    ? allStatuses.filter((status) => status !== currentStatus)
    : SERVICE_STATUS_TRANSITIONS[serviceType][currentStatus] || [];

  return statuses.map((status) => ({
    value: status,
    label: humanize(status),
  }));
};

const getDriverRequiredServiceCategory = (row?: CheckoutGroupRow | null) => {
  if (!row) return "delivery";
  return row.serviceType === "delivery" ? "delivery" : "product_order";
};

const isDriverEligibleForRow = (
  driver: DriverRow,
  row?: CheckoutGroupRow | null,
) => {
  const active = normalizeStatusKey(driver.status) === "active";
  const onShift = normalizeStatusKey(driver.currentShiftStatus) === "on_shift";
  const currentJobs = Math.max(0, Number(driver.currentJobsCount || 0));
  const maxJobs = Math.max(1, Number(driver.maxConcurrentJobs || 2));
  const requiredCategory = getDriverRequiredServiceCategory(row);
  const categories = Array.isArray(driver.serviceCategories)
    ? driver.serviceCategories.map(normalizeStatusKey)
    : [];
  const categoryAllowed =
    categories.length === 0 || categories.includes(requiredCategory);

  return active && onShift && currentJobs < maxJobs && categoryAllowed;
};

const cleanAccessValue = (value: unknown) => String(value || "").trim();

const normalizeAccessRole = (value: unknown) =>
  cleanAccessValue(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getInlineAccessRole = (user: any) =>
  user?.role ||
  user?.userRole ||
  user?.accountRole ||
  user?.adminRole ||
  user?.claims?.role ||
  user?.customClaims?.role ||
  "";

const isSuperAdminRole = (value: unknown) =>
  normalizeAccessRole(value) === "superadmin";

const hasAdminStatusAccess = (value: unknown) =>
  ["admin", "superadmin"].includes(normalizeAccessRole(value));

const extractResponseObject = <T,>(payload: any): T => {
  if (payload?.data && !Array.isArray(payload.data)) return payload.data as T;
  return payload as T;
};

const extractResponseArray = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.waybills)) return payload.waybills;
  if (Array.isArray(payload?.jobs)) return payload.jobs;
  if (Array.isArray(payload?.drivers)) return payload.drivers;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
  if (Array.isArray(payload?.data?.waybills)) return payload.data.waybills;
  if (Array.isArray(payload?.data?.jobs)) return payload.data.jobs;
  if (Array.isArray(payload?.data?.drivers)) return payload.data.drivers;

  return [];
};

const getOrderAccountIds = (order: Order) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = cleanAccessValue(value);
    if (id) ids.add(id);
  };

  // Seller/owner fields are authoritative for an admin-facing order.
  add(order.ownerUserId);
  add(order.sellerUserId);

  (order.items || []).forEach((item) => {
    add(item.sellerUserId);
    add(item.ownerId);
  });

  // Older orders may only contain userId. Use it only when no seller/owner
  // relationship exists, so a buyer does not accidentally become the owner.
  if (ids.size === 0) {
    add(order.userId);
    (order.items || []).forEach((item) => add(item.userId));
  }

  return ids;
};

const isOrderOwnedByAccount = (order: Order, userId: string) => {
  const uid = cleanAccessValue(userId);
  return Boolean(uid) && getOrderAccountIds(order).has(uid);
};

const isWaybillOwnedByAccount = (waybill: Waybill, userId: string) =>
  Boolean(cleanAccessValue(userId)) &&
  cleanAccessValue(waybill.sellerUserId) === cleanAccessValue(userId);

const isDispatchJobOwnedByAccount = (job: DispatchJob, userId: string) =>
  Boolean(cleanAccessValue(userId)) &&
  cleanAccessValue(job.ownerUserId) === cleanAccessValue(userId);

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const currentUserUid = cleanAccessValue(user?.uid);
  const [resolvedAccessRole, setResolvedAccessRole] = useState(() =>
    getInlineAccessRole(user),
  );
  const [accessLoading, setAccessLoading] = useState(true);

  const isSuperAdmin = isSuperAdminRole(resolvedAccessRole);
  const canAccessAllServiceStatuses = hasAdminStatusAccess(resolvedAccessRole);

  const [orders, setOrders] = useState<Order[]>([]);
  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [dispatchJobs, setDispatchJobs] = useState<DispatchJob[]>([]);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [statusOptionsByOrderId, setStatusOptionsByOrderId] = useState<
    Record<string, StatusOption[]>
  >({});
  const [statusOptionsLoadingByOrderId, setStatusOptionsLoadingByOrderId] =
    useState<Record<string, boolean>>({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const [assignModalRow, setAssignModalRow] = useState<CheckoutGroupRow | null>(
    null,
  );
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [candidateRefreshLoading, setCandidateRefreshLoading] = useState(false);
  const [assignModalJob, setAssignModalJob] = useState<DispatchJob | null>(
    null,
  );
  const [driverSearch, setDriverSearch] = useState("");

  const [search, setSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [waybillStatusFilter, setWaybillStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const canManageOrder = (order: Order) =>
    isSuperAdmin || isOrderOwnedByAccount(order, currentUserUid);

  const showAccessDenied = () => {
    toast({
      title: "Access denied",
      description: "You can only manage orders linked to your account.",
      status: "error",
      duration: 3500,
      isClosable: true,
    });
  };

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    deepSlate: "#2a2c3b",
    deepInk: "#1a1c2f",
    slate2: "#2e3143",
    softLight: "#eaebf0",
    violet: "#b05cff",
    indigo: "#6f7cff",
  };

  const gradients = theme.gradients || {
    pageBg: theme.background,
    shellBg: theme.surface,
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
  };

  const pageBg = gradients.pageBg || theme.background;
  const shellBg = gradients.shellBg || theme.surface;
  const cardBg = theme.surface;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const panelShadow = theme.shadowMd;
  const softShadow = theme.shadowSm;

  const baseControlStyle = {
    border: `1px solid ${theme.border}`,
    borderRadius: "14px",
    boxShadow: softShadow,
    bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.92)",
    color: textColor,
    _hover: {
      borderColor: brand.electricBlue,
      bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,1)",
    },
    _focus: {
      borderColor: brand.electricBlue,
      boxShadow: `0 0 0 1px ${brand.electricBlue}`,
    },
  };

  const selectOptionSx = {
    option: {
      background: isDark ? "#1f2430" : "#ffffff",
      color: isDark ? "#f7fafc" : "#1a202c",
    },
  };

  useEffect(() => {
    setStatusOptionsByOrderId({});
  }, [resolvedAccessRole]);

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setResolvedAccessRole("");
          setAccessLoading(false);
          setOrders([]);
          setWaybills([]);
          setDispatchJobs([]);
        }
        return;
      }

      setAccessLoading(true);

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const profile = snapshot.exists() ? snapshot.data() : {};

        const role =
          profile?.role || profile?.userRole || getInlineAccessRole(user);

        if (!cancelled) {
          setResolvedAccessRole(String(role || ""));
        }
      } catch (accessError) {
        console.error("Failed to resolve Admin Orders access:", accessError);

        if (!cancelled) {
          setResolvedAccessRole(String(getInlineAccessRole(user) || ""));
        }
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const getAuthenticatedRequestConfig = async () => {
    try {
      const token = await user?.getIdToken?.();

      if (token) {
        return {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      }
    } catch (tokenError) {
      console.warn(
        "Failed to get Firebase token for admin request:",
        tokenError,
      );
    }

    return {};
  };

  const getAvailableStatusOptions = (order: Order): StatusOption[] => {
    const cached = statusOptionsByOrderId[order._id];

    if (cached) return cached;

    return getFallbackStatusOptions({
      order,
      role: canAccessAllServiceStatuses ? resolvedAccessRole : "",
    });
  };

  const loadOrderStatusOptions = async (order: Order, force = false) => {
    const orderId = String(order._id || "").trim();

    if (!orderId) return;
    if (!force && statusOptionsByOrderId[orderId]) return;
    if (statusOptionsLoadingByOrderId[orderId]) return;

    setStatusOptionsLoadingByOrderId((previous) => ({
      ...previous,
      [orderId]: true,
    }));

    try {
      const config = await getAuthenticatedRequestConfig();
      const response = await axios.get(
        `${API_BASE}/api/v1/orders/${orderId}/status-options`,
        config,
      );
      const payload = extractResponseObject<StatusOptionsResponse>(
        response.data,
      );
      const options = Array.isArray(payload?.statusOptions)
        ? payload.statusOptions
            .filter((option) => option?.value)
            .map((option) => ({
              value: normalizeStatusKey(option.value),
              label: option.label || humanize(option.value),
              description: option.description,
              terminal: option.terminal,
            }))
        : [];

      setStatusOptionsByOrderId((previous) => ({
        ...previous,
        [orderId]:
          options.length > 0
            ? options
            : getFallbackStatusOptions({
                order,
                role: canAccessAllServiceStatuses ? resolvedAccessRole : "",
              }),
      }));
    } catch (statusOptionsError) {
      console.warn("Failed to load service status options:", {
        orderId,
        serviceType: resolveOrderServiceType(order),
        error: statusOptionsError,
      });

      setStatusOptionsByOrderId((previous) => ({
        ...previous,
        [orderId]: getFallbackStatusOptions({
          order,
          role: canAccessAllServiceStatuses ? resolvedAccessRole : "",
        }),
      }));
    } finally {
      setStatusOptionsLoadingByOrderId((previous) => ({
        ...previous,
        [orderId]: false,
      }));
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError("");

      if (!isSuperAdmin && !currentUserUid) {
        setOrders([]);
        setWaybills([]);
        return;
      }

      const config = await getAuthenticatedRequestConfig();
      const [ordersRes, waybillsRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/v1/orders`, config),
        axios.get(`${API_BASE}/api/v1/waybills`, config),
      ]);

      if (ordersRes.status !== "fulfilled") {
        throw ordersRes.reason;
      }

      const allOrders = extractResponseArray<Order>(ordersRes.value.data);
      const accountOrders = isSuperAdmin
        ? allOrders
        : allOrders.filter((order) =>
            isOrderOwnedByAccount(order, currentUserUid),
          );

      setOrders(accountOrders);

      if (waybillsRes.status === "fulfilled") {
        const allWaybills = extractResponseArray<Waybill>(
          waybillsRes.value.data,
        );

        const visibleOrderIds = new Set(
          accountOrders
            .map((order) => cleanAccessValue(order._id))
            .filter(Boolean),
        );
        const visibleCheckoutGroupIds = new Set(
          accountOrders
            .map((order) => cleanAccessValue(order.checkoutGroupId))
            .filter(Boolean),
        );

        const accountWaybills = isSuperAdmin
          ? allWaybills
          : allWaybills.filter(
              (waybill) =>
                isWaybillOwnedByAccount(waybill, currentUserUid) ||
                visibleOrderIds.has(cleanAccessValue(waybill.orderId)) ||
                visibleCheckoutGroupIds.has(
                  cleanAccessValue(waybill.checkoutGroupId),
                ),
            );

        setWaybills(accountWaybills);
      } else {
        console.warn("Failed to load waybills:", waybillsRes.reason);
        setWaybills([]);
      }
    } catch (err) {
      console.error("Failed to load orders:", err);
      setOrders([]);
      setWaybills([]);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const loadDispatchData = async () => {
    try {
      if (!isSuperAdmin && !currentUserUid) {
        setDispatchJobs([]);
        setDrivers([]);
        return;
      }

      const config = await getAuthenticatedRequestConfig();
      const [jobsRes, driversRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/v1/dispatch/jobs`, config),
        axios.get(`${API_BASE}/api/v1/drivers`, config),
      ]);

      const visibleOrderIds = new Set(
        orders.map((order) => cleanAccessValue(order._id)).filter(Boolean),
      );
      const visibleCheckoutGroupIds = new Set(
        orders
          .map((order) => cleanAccessValue(order.checkoutGroupId))
          .filter(Boolean),
      );

      if (jobsRes.status === "fulfilled") {
        const allJobs = extractResponseArray<DispatchJob>(jobsRes.value.data);
        const accountJobs = isSuperAdmin
          ? allJobs
          : allJobs.filter(
              (job) =>
                isDispatchJobOwnedByAccount(job, currentUserUid) ||
                visibleOrderIds.has(cleanAccessValue(job.sourceId)) ||
                visibleCheckoutGroupIds.has(cleanAccessValue(job.sourceId)),
            );

        setDispatchJobs(accountJobs);
      } else {
        console.warn("Failed to load dispatch jobs:", jobsRes.reason);
        setDispatchJobs([]);
      }

      if (driversRes.status === "fulfilled") {
        setDrivers(extractResponseArray<DriverRow>(driversRes.value.data));
      } else {
        console.warn("Failed to load drivers:", driversRes.reason);
        setDrivers([]);
      }
    } catch (err) {
      console.error("Failed to load dispatch assignment data:", err);
      setDispatchJobs([]);
      setDrivers([]);
    }
  };

  useEffect(() => {
    if (accessLoading) return;

    if (!isSuperAdmin && !currentUserUid) {
      setOrders([]);
      setWaybills([]);
      setLoading(false);
      return;
    }

    void loadOrders();
    // loadOrders deliberately reruns when the resolved viewer access changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, currentUserUid, isSuperAdmin]);

  useEffect(() => {
    if (accessLoading) return;
    if (!isSuperAdmin && !currentUserUid) return;

    void loadDispatchData();
    // Dispatch is refreshed whenever the visible order collection changes so
    // the card shows the real assigned/pickup/transit/delivery state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, currentUserUid, isSuperAdmin, orders]);

  const waybillsByGroup = useMemo(() => {
    const map = new Map<string, Waybill[]>();

    waybills.forEach((waybill) => {
      const key = String(
        waybill.checkoutGroupId || waybill.orderId || "",
      ).trim();
      if (!key) return;

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(waybill);
    });

    return map;
  }, [waybills]);

  const dispatchJobsBySourceId = useMemo(() => {
    const map = new Map<string, DispatchJob[]>();

    dispatchJobs.forEach((job) => {
      const sourceId = String(job.sourceId || "").trim();
      if (!sourceId) return;

      if (!map.has(sourceId)) map.set(sourceId, []);
      map.get(sourceId)!.push(job);
    });

    return map;
  }, [dispatchJobs]);

  const dispatchJobsById = useMemo(() => {
    const map = new Map<string, DispatchJob>();

    dispatchJobs.forEach((job) => {
      const id = String(job._id || "").trim();
      if (id) map.set(id, job);
    });

    return map;
  }, [dispatchJobs]);

  const checkoutRows = useMemo<CheckoutGroupRow[]>(() => {
    const map = new Map<string, Order[]>();

    orders.forEach((order) => {
      const key = getCheckoutKey(order);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(order);
    });

    return Array.from(map.entries()).map(([key, groupOrders]) => {
      const primaryOrder = groupOrders[0];

      const groupWaybills =
        waybillsByGroup.get(key) ||
        groupOrders.flatMap(
          (order) => waybillsByGroup.get(String(order._id)) || [],
        );

      const sellerNames = Array.from(
        new Set(
          groupOrders
            .map((order) =>
              String(
                order.sellerName ||
                  order.sellerUserId ||
                  order.ownerUserId ||
                  "",
              ).trim(),
            )
            .filter(Boolean),
        ),
      );

      const total = groupOrders.reduce(
        (sum, order) => sum + Number(order.total || 0),
        0,
      );

      const totalItems = groupOrders.reduce((sum, order) => {
        return (
          sum +
          (order.items || []).reduce(
            (itemSum, item) => itemSum + Number(item.quantity || 1),
            0,
          )
        );
      }, 0);

      const serviceType = getGroupServiceType(groupOrders);
      const groupDispatchJobsMap = new Map<string, DispatchJob>();

      groupOrders.forEach((groupOrder) => {
        (dispatchJobsBySourceId.get(String(groupOrder._id)) || []).forEach(
          (job) => groupDispatchJobsMap.set(String(job._id), job),
        );

        const trackingJobId = String(
          groupOrder.deliveryTracking?.dispatchJobId || "",
        ).trim();
        const trackingJob = trackingJobId
          ? dispatchJobsById.get(trackingJobId)
          : undefined;

        if (trackingJob) {
          groupDispatchJobsMap.set(String(trackingJob._id), trackingJob);
        }
      });

      const groupDispatchJobs = Array.from(groupDispatchJobsMap.values());
      const hasDeliveryWork = groupOrders.some(isDeliveryOrder);
      const rowHasDispatchJob = hasDispatchJob(
        groupOrders,
        groupWaybills,
        groupDispatchJobs,
      );
      const rowHasAssignedDriver = hasAssignedDriver(
        groupOrders,
        groupWaybills,
        groupDispatchJobs,
      );

      return {
        key,
        primaryOrder,
        orders: groupOrders,
        waybills: groupWaybills,
        dispatchJobs: groupDispatchJobs,
        serviceType,
        email: primaryOrder?.email || "",
        customerName: primaryOrder?.name || "",
        customerPhone: primaryOrder?.phoneNumber || "",
        buyerUserId: primaryOrder?.userId || "",
        checkoutGroupId: primaryOrder?.checkoutGroupId || "",
        orderCount: groupOrders.length,
        sellerCount: sellerNames.length || groupOrders.length,
        sellerNames,
        total,
        status: getGroupStatus(groupOrders),
        paymentStatus: getGroupPaymentStatus(groupOrders),
        dispatchStatus: getDispatchStatus(
          groupOrders,
          groupWaybills,
          groupDispatchJobs,
        ),
        hasDeliveryWork,
        hasDispatchJob: rowHasDispatchJob,
        hasAssignedDriver: rowHasAssignedDriver,
        topCategory: getTopCategory(groupOrders),
        totalItems,
        createdAt: getCreatedDate(groupOrders),
      };
    });
  }, [orders, waybillsByGroup, dispatchJobsBySourceId, dispatchJobsById]);

  const stats = useMemo(() => {
    const paid = checkoutRows.filter((row) =>
      isPaymentConfirmed(row.paymentStatus),
    ).length;
    const needsPayment = checkoutRows.filter(
      (row) => !isPaymentConfirmed(row.paymentStatus),
    ).length;
    const needsDriver = checkoutRows.filter(
      (row) => row.hasDeliveryWork && !row.hasAssignedDriver,
    ).length;
    const totalValue = checkoutRows.reduce(
      (sum, row) => sum + Number(row.total || 0),
      0,
    );

    return {
      groups: checkoutRows.length,
      paid,
      needsPayment,
      needsDriver,
      totalValue,
    };
  }, [checkoutRows]);

  const updateOrderProgress = async (
    row: CheckoutGroupRow,
    orderId: string,
    status: string,
    userId?: string,
  ) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    const blockedReason = getBlockedStatusReason(row, status);

    if (blockedReason) {
      toast({
        title: "Order progress blocked",
        description: blockedReason,
        status: "warning",
        duration: 4500,
        isClosable: true,
      });
      return;
    }

    if (
      status === "delivered" &&
      row.hasDeliveryWork &&
      !isPaymentConfirmed(row.paymentStatus)
    ) {
      const confirmed = window.confirm(
        "This delivery is not marked as paid yet. You can deliver it, but driver payout will not become eligible until payment/cash is confirmed. Continue?",
      );

      if (!confirmed) return;
    }

    try {
      setActionBusyKey(`${orderId}:order:${status}`);

      const config = await getAuthenticatedRequestConfig();

      await axios.patch(
        `${API_BASE}/api/v1/orders/${orderId}/status`,
        {
          status,
          userId,
          source: "admin_orders_page",
        },
        config,
      );

      toast({
        title: "Order progress updated",
        description: `Fulfilment moved to ${humanize(status)}.`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });

      setStatusOptionsByOrderId((previous) => {
        const next = { ...previous };
        delete next[orderId];
        return next;
      });

      await loadOrders();
    } catch (err: any) {
      console.error("Failed to update order progress:", err);

      toast({
        title: "Failed to update order progress",
        description:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Please check backend logs.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionBusyKey("");
    }
  };

  const markPaymentPaid = async (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    const order = row.primaryOrder;

    if (isPaymentConfirmed(row.paymentStatus)) {
      toast({
        title: "Payment already confirmed",
        description: "This order already has a paid payment status.",
        status: "info",
        duration: 2400,
        isClosable: true,
      });
      return;
    }

    try {
      setActionBusyKey(`${order._id}:payment:paid`);

      // Non-breaking: uses the existing backend status endpoint.
      // The backend sets order.payment.status = "paid" when status = "paid".
      const config = await getAuthenticatedRequestConfig();

      await axios.patch(
        `${API_BASE}/api/v1/orders/${order._id}/status`,
        {
          status: "paid",
          userId: order.userId,
          source: "admin_orders_page",
        },
        config,
      );

      toast({
        title: "Payment confirmed",
        description:
          "Invoice is now marked as paid and payment lifecycle notifications can run.",
        status: "success",
        duration: 2800,
        isClosable: true,
      });

      await loadOrders();
    } catch (err: any) {
      console.error("Failed to mark payment paid:", err);

      toast({
        title: "Failed to confirm payment",
        description:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Please check backend logs.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionBusyKey("");
    }
  };

  const openInvoice = (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    const orderId = row.primaryOrder._id;

    window.open(`${API_BASE}/api/v1/invoices/open/${orderId}`, "_blank");
  };

  const downloadInvoice = (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    const orderId = row.primaryOrder._id;

    window.open(`${API_BASE}/api/v1/invoices/download/${orderId}`, "_blank");
  };

  const openDispatchForRow = (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    navigate("/drivers/admin/dispatch", {
      state: {
        orderId: row.primaryOrder._id,
        checkoutGroupId: row.checkoutGroupId || row.key,
        category: row.topCategory,
        serviceType: row.serviceType,
        requiresDriver: row.hasDeliveryWork,
      },
    });
  };

  const openAssignDriverModal = async (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      showAccessDenied();
      return;
    }

    try {
      setAssignLoading(true);
      setSelectedDriverId("");
      setDriverSearch("");
      setAssignModalJob(null);

      // Create or reuse the dispatch job first. Keep the returned job in local
      // modal state so the picker does not depend on a later React state refresh.
      const job = await createDispatchJobForRow(row);
      const config = await getAuthenticatedRequestConfig();

      let refreshedJob = job;
      try {
        const refreshResponse = await axios.post(
          `${API_BASE}/api/v1/dispatch/jobs/${job._id}/refresh-candidates`,
          { expandRadius: false },
          config,
        );
        refreshedJob = refreshResponse.data?.data || job;
      } catch (candidateError) {
        // Assignment must still be usable even when candidate refresh fails.
        // The full driver list remains searchable and backend claim validation
        // is still the final source of truth.
        console.warn(
          "Failed to refresh recommended assignment candidates:",
          candidateError,
        );
      }

      setAssignModalJob(refreshedJob);
      await loadDispatchData();
      setAssignModalRow(row);
    } catch (err: any) {
      console.error("Failed to prepare driver assignment:", err);
      toast({
        title: "Failed to prepare driver assignment",
        description:
          err?.response?.data?.message ||
          err?.message ||
          "Please check the PickDengu driver assignments and backend logs.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssignDriverModal = () => {
    if (assignLoading) return;
    setAssignModalRow(null);
    setAssignModalJob(null);
    setSelectedDriverId("");
    setDriverSearch("");
  };

  const getExistingDispatchJobForRow = (row: CheckoutGroupRow) => {
    const trackingJobId = String(
      row.primaryOrder.deliveryTracking?.dispatchJobId || "",
    ).trim();

    if (trackingJobId) {
      const byTracking = dispatchJobs.find(
        (job) => String(job._id) === trackingJobId,
      );
      if (byTracking) return byTracking;

      return {
        _id: trackingJobId,
        sourceType: "order" as const,
        sourceId: row.primaryOrder._id,
      };
    }

    return dispatchJobs.find(
      (job) =>
        String(job.sourceType || "") === "order" &&
        String(job.sourceId || "") === String(row.primaryOrder._id),
    );
  };

  const refreshAssignmentCandidates = async () => {
    if (!assignModalRow) return;

    try {
      setCandidateRefreshLoading(true);

      const existingJob =
        assignModalJob || getExistingDispatchJobForRow(assignModalRow);
      const job = existingJob?._id
        ? existingJob
        : await createDispatchJobForRow(assignModalRow);
      const config = await getAuthenticatedRequestConfig();
      const response = await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${job._id}/refresh-candidates`,
        { expandRadius: false },
        config,
      );

      const refreshedJob = response.data?.data || job;
      setAssignModalJob(refreshedJob);
      await loadDispatchData();

      toast({
        title: "Driver list refreshed",
        description:
          "Recommended warehouse and nearby candidates were refreshed.",
        status: "success",
        duration: 1800,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Could not refresh recommended drivers",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "The existing searchable driver list is still available.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setCandidateRefreshLoading(false);
    }
  };

  const openLiveDriverMapForAssignment = () => {
    const job =
      assignModalJob ||
      (assignModalRow
        ? getExistingDispatchJobForRow(assignModalRow)
        : undefined);
    const jobId = String(job?._id || "").trim();

    navigate(
      jobId
        ? `/drivers/admin/live-map?jobId=${encodeURIComponent(jobId)}`
        : "/drivers/admin/live-map",
    );
  };

  const createDispatchJobForRow = async (row: CheckoutGroupRow) => {
    if (!canManageOrder(row.primaryOrder)) {
      throw new Error(
        "You can only create dispatch work for orders linked to your account.",
      );
    }

    const order = row.primaryOrder;
    const isDirectDelivery = row.serviceType === "delivery";
    const requesterUserId = String(
      order.userId || row.buyerUserId || order.ownerUserId || "",
    ).trim();
    const merchantOwnerUserId = String(
      order.ownerUserId || order.sellerUserId || "",
    ).trim();

    if (!isDirectDelivery && !merchantOwnerUserId) {
      throw new Error(
        "Cannot create merchant dispatch because the seller/owner id is missing.",
      );
    }

    const payload = isDirectDelivery
      ? {
          sourceType: "order",
          sourceId: order._id,
          requesterUserId,
          dispatchScope: "platform_direct",
          pickupAddress: String(order.fromAddress || ""),
          pickupLatLng: {
            lat: order.fromLocation?.lat,
            lng: order.fromLocation?.lng,
          },
          dropoffAddress: String(order.toAddress || ""),
          dropoffLatLng: {
            lat: order.toLocation?.lat,
            lng: order.toLocation?.lng,
          },
          customerName: String(order.name || row.customerName || ""),
          customerPhone: String(order.phoneNumber || row.customerPhone || ""),
          orderValue: Number(order.total || 0),
          deliveryFee: Number(order.pricingBreakdown?.deliveryFee || 0),
          driverEarning: Number(
            order.pricingBreakdown?.driverEarningTotal || 0,
          ),
          cashOnDelivery:
            String(order.payment?.method || "").toLowerCase() === "cash" ||
            String(order.payment?.status || "").toLowerCase() ===
              "cash_expected",
          orderType: "delivery",
          proofOfDeliveryRequired: true,
        }
      : {
          sourceType: "order",
          sourceId: order._id,
          ownerUserId: merchantOwnerUserId,
          dispatchScope: "merchant_warehouse",
          warehouseId: String(
            (order as any).dispatchWarehouseId ||
              (order as any).pickupWarehouseId ||
              "",
          ),
          warehouseName: String(order.sellerName || "Order Dispatch"),
          warehouseSnapshot: {
            address: String(order.fromAddress || ""),
            lat: order.fromLocation?.lat,
            lng: order.fromLocation?.lng,
          },
          pickupAddress: String(order.fromAddress || ""),
          pickupLatLng: {
            lat: order.fromLocation?.lat,
            lng: order.fromLocation?.lng,
          },
          dropoffAddress: String(order.toAddress || ""),
          dropoffLatLng: {
            lat: order.toLocation?.lat,
            lng: order.toLocation?.lng,
          },
          customerName: String(order.name || row.customerName || ""),
          customerPhone: String(order.phoneNumber || row.customerPhone || ""),
          orderValue: Number(order.total || 0),
          deliveryFee: Number(order.pricingBreakdown?.deliveryFee || 0),
          driverEarning: Number(
            order.pricingBreakdown?.driverEarningTotal || 0,
          ),
          cashOnDelivery:
            String(order.payment?.method || "").toLowerCase() === "cash" ||
            String(order.payment?.status || "").toLowerCase() ===
              "cash_expected",
          orderType: "product_order",
          proofOfDeliveryRequired: true,
        };

    const config = await getAuthenticatedRequestConfig();
    const res = await axios.post(
      `${API_BASE}/api/v1/dispatch/jobs`,
      payload,
      config,
    );
    const job = res.data?.data;

    if (!job?._id) {
      throw new Error("Dispatch job was not created.");
    }

    return job as DispatchJob;
  };

  const assignSelectedDriver = async () => {
    if (!assignModalRow) return;

    if (!canManageOrder(assignModalRow.primaryOrder)) {
      showAccessDenied();
      closeAssignDriverModal();
      return;
    }

    if (!selectedDriverId) {
      toast({
        title: "Choose a driver",
        description: "Please select a driver before assigning.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setAssignLoading(true);

      const existingJob =
        assignModalJob || getExistingDispatchJobForRow(assignModalRow);
      const job = existingJob?._id
        ? existingJob
        : await createDispatchJobForRow(assignModalRow);

      const config = await getAuthenticatedRequestConfig();

      await axios.post(
        `${API_BASE}/api/v1/dispatch/jobs/${job._id}/claim`,
        {
          driverId: selectedDriverId,
          assignmentSource: "admin_assignment",
          allowNonCandidate: true,
        },
        config,
      );

      toast({
        title: "Driver assigned",
        description: "The driver has been assigned to this delivery.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      await Promise.all([loadOrders(), loadDispatchData()]);
      closeAssignDriverModal();
    } catch (err: any) {
      console.error("Failed to assign driver:", err);

      toast({
        title: "Failed to assign driver",
        description:
          err?.response?.data?.message ||
          err?.message ||
          "Please check backend logs.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: `${label} copied`,
        status: "success",
        duration: 1200,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Copy failed",
        status: "error",
        duration: 1600,
        isClosable: true,
      });
    }
  };

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const filteredRows = useMemo(() => {
    let data = [...checkoutRows];
    const q = search.trim().toLowerCase();

    if (q) {
      data = data.filter((row) => {
        const orderIds = row.orders
          .map((o) => o._id)
          .join(" ")
          .toLowerCase();
        const sellerNames = row.sellerNames.join(" ").toLowerCase();
        const itemNames = row.orders
          .flatMap((o) => o.items || [])
          .map((item) => getOrderItemName(item).toLowerCase())
          .join(" ");

        return (
          row.email.toLowerCase().includes(q) ||
          row.customerName.toLowerCase().includes(q) ||
          row.customerPhone.toLowerCase().includes(q) ||
          row.status.toLowerCase().includes(q) ||
          row.paymentStatus.toLowerCase().includes(q) ||
          row.dispatchStatus.toLowerCase().includes(q) ||
          row.key.toLowerCase().includes(q) ||
          row.checkoutGroupId.toLowerCase().includes(q) ||
          orderIds.includes(q) ||
          sellerNames.includes(q) ||
          itemNames.includes(q)
        );
      });
    }

    if (orderStatusFilter !== "all") {
      data = data.filter((row) =>
        row.orders.some(
          (order) =>
            String(order.status || "") === orderStatusFilter ||
            normalizeOrderProgressStatus(order.status) === orderStatusFilter,
        ),
      );
    }

    if (paymentStatusFilter !== "all") {
      data = data.filter((row) => row.paymentStatus === paymentStatusFilter);
    }

    if (waybillStatusFilter !== "all") {
      data = data.filter((row) =>
        row.waybills.some(
          (waybill) => String(waybill.status || "") === waybillStatusFilter,
        ),
      );
    }

    data.sort((a, b) => {
      if (sortBy === "amount-high")
        return Number(b.total || 0) - Number(a.total || 0);
      if (sortBy === "amount-low")
        return Number(a.total || 0) - Number(b.total || 0);
      if (sortBy === "customer-asc") {
        return (a.customerName || a.email || "").localeCompare(
          b.customerName || b.email || "",
        );
      }
      if (sortBy === "customer-desc") {
        return (b.customerName || b.email || "").localeCompare(
          a.customerName || a.email || "",
        );
      }

      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;

      if (sortBy === "oldest") return aDate - bDate;
      return bDate - aDate;
    });

    return data;
  }, [
    checkoutRows,
    search,
    orderStatusFilter,
    paymentStatusFilter,
    waybillStatusFilter,
    sortBy,
  ]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = usePagination<CheckoutGroupRow>(filteredRows, 10);

  useEffect(() => {
    setCurrentPage(1);
    // Only reset when the user changes search, filters, or sorting.
    // Do not depend on a reset callback from the pagination hook because
    // an unstable callback reference can reset every page click back to 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    orderStatusFilter,
    paymentStatusFilter,
    waybillStatusFilter,
    sortBy,
  ]);

  useEffect(() => {
    const safePageSize = Math.max(1, Number(pageSize) || 1);
    const lastPage = Math.max(1, Math.ceil(totalItems / safePageSize));

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, totalItems]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const assignmentCandidateJob = useMemo(
    () =>
      assignModalJob ||
      (assignModalRow
        ? getExistingDispatchJobForRow(assignModalRow)
        : undefined),
    [assignModalJob, assignModalRow, dispatchJobs],
  );

  const assignmentCandidateIds = useMemo(
    () =>
      new Set(
        Array.isArray(assignmentCandidateJob?.candidateDriverIds)
          ? assignmentCandidateJob!.candidateDriverIds!.map((value) =>
              String(value || "").trim(),
            )
          : [],
      ),
    [assignmentCandidateJob],
  );

  const assignmentCandidateDetails = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<DispatchJob["candidateDriverDetails"]>[number]
    >();

    (assignmentCandidateJob?.candidateDriverDetails || []).forEach((detail) => {
      const driverId = String(detail?.driverId || "").trim();
      if (driverId) map.set(driverId, detail);
    });

    return map;
  }, [assignmentCandidateJob]);

  const filteredDrivers = useMemo(() => {
    const q = driverSearch.trim().toLowerCase();

    return [...drivers]
      .filter((driver) => {
        if (!q) return true;

        return `${driver.name || ""} ${driver.phoneNumber || ""} ${
          driver.whatsappNumber || ""
        } ${driver.vehicleType || ""} ${driver.id || ""} ${(
          driver.serviceCategories || []
        ).join(" ")}`
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const aRecommended = assignmentCandidateIds.has(String(a.id));
        const bRecommended = assignmentCandidateIds.has(String(b.id));
        if (aRecommended !== bRecommended) return aRecommended ? -1 : 1;

        const aEligible = isDriverEligibleForRow(a, assignModalRow);
        const bEligible = isDriverEligibleForRow(b, assignModalRow);
        if (aEligible !== bEligible) return aEligible ? -1 : 1;

        const aDetail = assignmentCandidateDetails.get(String(a.id));
        const bDetail = assignmentCandidateDetails.get(String(b.id));
        const aDistance =
          aDetail?.distanceToPickupKm == null
            ? Number.POSITIVE_INFINITY
            : Number(aDetail.distanceToPickupKm);
        const bDistance =
          bDetail?.distanceToPickupKm == null
            ? Number.POSITIVE_INFINITY
            : Number(bDetail.distanceToPickupKm);
        if (aDistance !== bDistance) return aDistance - bDistance;

        const aJobs = Number(a.currentJobsCount || 0);
        const bJobs = Number(b.currentJobsCount || 0);
        if (aJobs !== bJobs) return aJobs - bJobs;

        return Number(b.ratingScore || 0) - Number(a.ratingScore || 0);
      });
  }, [
    drivers,
    driverSearch,
    assignModalRow,
    assignmentCandidateIds,
    assignmentCandidateDetails,
  ]);

  return (
    <Box h="100%" minH="100%" w="100%" bg={pageBg}>
      <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
        <Box
          flex="1"
          minH={0}
          w="100%"
          borderRadius={{ base: "18px", md: "22px" }}
          bg={shellBg}
          boxShadow={panelShadow}
          p={{ base: 3, md: 5 }}
          backdropFilter={isDark ? "blur(10px)" : undefined}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            mb={4}
          >
            <Box>
              <Heading size="lg" color={textColor}>
                Admin Orders
              </Heading>
              <Text color={mutedText} fontWeight="500" mt={1}>
                Update payment, move fulfilment forward, assign drivers, and
                open dispatch without mixing workflows.
              </Text>
            </Box>

            <Button
              size="sm"
              onClick={loadOrders}
              color={theme.textOnPrimary}
              bgImage={gradients.primaryButton}
              _hover={{ bgImage: gradients.primaryButtonHover }}
              boxShadow={softShadow}
              isLoading={loading || accessLoading}
            >
              Refresh
            </Button>
          </Flex>

          {!loading && !accessLoading && !error && (
            <SimpleGrid columns={{ base: 1, md: 5 }} spacing={3} mb={4}>
              <StatCard label="Checkout Groups" value={stats.groups} />
              <StatCard
                label="Payment Confirmed"
                value={stats.paid}
                tone="success"
              />
              <StatCard
                label="Needs Payment"
                value={stats.needsPayment}
                tone={stats.needsPayment > 0 ? "warning" : "normal"}
              />
              <StatCard
                label="Needs Driver"
                value={stats.needsDriver}
                tone={stats.needsDriver > 0 ? "warning" : "normal"}
              />
              <StatCard
                label="Total Value"
                value={formatMoney(stats.totalValue)}
              />
            </SimpleGrid>
          )}

          {(loading || accessLoading) && (
            <Flex
              minH="260px"
              justify="center"
              align="center"
              direction="column"
              gap={3}
              borderRadius="18px"
              bg={cardBg}
              boxShadow={softShadow}
              border={`1px solid ${theme.border}`}
            >
              <GlobalSpinner />
              <Text color={mutedText} fontWeight="500">
                Loading orders...
              </Text>
            </Flex>
          )}

          {error && (
            <Box
              p={4}
              borderRadius="16px"
              border="1px solid"
              borderColor={isDark ? "rgba(245,101,101,0.25)" : "red.200"}
              bg={isDark ? "rgba(245,101,101,0.12)" : "red.50"}
              boxShadow={softShadow}
            >
              <Text color={isDark ? "red.200" : "red.600"} fontWeight="700">
                {error}
              </Text>
            </Box>
          )}

          {!loading && !accessLoading && !error && (
            <>
              <Box
                borderRadius="18px"
                bg={cardBg}
                boxShadow={softShadow}
                border={`1px solid ${theme.border}`}
                p={{ base: 3, md: 4 }}
                mb={4}
              >
                <Text color={textColor} fontWeight="800" mb={3}>
                  Find and focus
                </Text>

                <Flex
                  gap={3}
                  wrap="wrap"
                  align="center"
                  direction={{ base: "column", md: "row" }}
                >
                  <Input
                    placeholder="Search customer, phone, order ID, seller, product..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    maxW={{ base: "100%", md: "440px" }}
                    {...baseControlStyle}
                  />

                  <Select
                    value={paymentStatusFilter}
                    onChange={(e) => setPaymentStatusFilter(e.target.value)}
                    maxW={{ base: "100%", md: "210px" }}
                    {...baseControlStyle}
                    sx={selectOptionSx}
                  >
                    <option value="all">All payment statuses</option>
                    {PAYMENT_FILTER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {humanize(s)}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={orderStatusFilter}
                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                    maxW={{ base: "100%", md: "210px" }}
                    {...baseControlStyle}
                    sx={selectOptionSx}
                  >
                    <option value="all">All order progress</option>
                    {ORDER_FILTER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {humanize(s)}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={waybillStatusFilter}
                    onChange={(e) => setWaybillStatusFilter(e.target.value)}
                    maxW={{ base: "100%", md: "220px" }}
                    {...baseControlStyle}
                    sx={selectOptionSx}
                  >
                    <option value="all">All dispatch statuses</option>
                    {WAYBILL_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {humanize(s)}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    maxW={{ base: "100%", md: "190px" }}
                    {...baseControlStyle}
                    sx={selectOptionSx}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="amount-high">Amount high → low</option>
                    <option value="amount-low">Amount low → high</option>
                    <option value="customer-asc">Customer A → Z</option>
                    <option value="customer-desc">Customer Z → A</option>
                  </Select>
                </Flex>
              </Box>

              <VStack spacing={4} align="stretch">
                {paginatedItems.map((row) => {
                  const primaryOrder = row.primaryOrder;
                  const expanded = Boolean(expandedRows[row.key]);
                  const currency = primaryOrder.currency || "MWK";
                  const firstItem = primaryOrder.items?.[0];
                  const paymentConfirmed = isPaymentConfirmed(
                    row.paymentStatus,
                  );
                  const progressStatus = normalizeOrderProgressStatus(
                    row.status,
                    row.serviceType,
                  );
                  const orderProgress = getOrderProgress(
                    row.serviceType,
                    progressStatus,
                  );
                  const availableStatusOptions =
                    getAvailableStatusOptions(primaryOrder);
                  const statusOptionsLoading = Boolean(
                    statusOptionsLoadingByOrderId[primaryOrder._id],
                  );
                  const busy = Boolean(actionBusyKey);

                  return (
                    <Box
                      key={row.key}
                      borderRadius="24px"
                      bg={cardBg}
                      border="1px solid"
                      borderColor={theme.border}
                      boxShadow={softShadow}
                      overflow="hidden"
                    >
                      <Box p={{ base: 4, md: 5 }}>
                        <Flex
                          align={{ base: "flex-start", xl: "center" }}
                          justify="space-between"
                          direction={{ base: "column", xl: "row" }}
                          gap={4}
                        >
                          <VStack align="stretch" spacing={3} flex="1" minW={0}>
                            <HStack spacing={2} flexWrap="wrap">
                              <Tooltip label={primaryOrder._id}>
                                <Text
                                  fontWeight="900"
                                  color={textColor}
                                  fontSize="lg"
                                >
                                  Order #{shortenId(primaryOrder._id)}
                                </Text>
                              </Tooltip>

                              <IconButton
                                aria-label="Copy order ID"
                                icon={<CopyIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  copy(primaryOrder._id, "Order ID")
                                }
                              />

                              <Badge
                                colorScheme="purple"
                                borderRadius="full"
                                px={2.5}
                                py={1}
                                textTransform="capitalize"
                              >
                                {humanize(row.serviceType)}
                              </Badge>

                              <Badge
                                colorScheme="blue"
                                borderRadius="full"
                                px={2.5}
                                py={1}
                              >
                                {row.totalItems} item
                                {row.totalItems === 1 ? "" : "s"}
                              </Badge>

                              {row.checkoutGroupId && (
                                <Badge
                                  colorScheme="gray"
                                  borderRadius="full"
                                  px={2.5}
                                  py={1}
                                >
                                  Group {shortenId(row.checkoutGroupId)}
                                </Badge>
                              )}
                            </HStack>

                            <SimpleGrid
                              columns={{ base: 1, md: 3 }}
                              spacing={3}
                            >
                              <MiniInfo
                                label="Customer"
                                value={
                                  row.customerName || row.email || "Customer"
                                }
                                helper={
                                  row.customerPhone || row.email || "No contact"
                                }
                              />
                              <MiniInfo
                                label="Order value"
                                value={formatMoney(row.total, currency)}
                                helper={formatDate(row.createdAt)}
                              />
                              <MiniInfo
                                label="First item"
                                value={getOrderItemName(firstItem || {})}
                                helper={
                                  row.totalItems > 1
                                    ? `+${row.totalItems - 1} more item(s)`
                                    : "Single item"
                                }
                              />
                            </SimpleGrid>
                          </VStack>

                          <HStack spacing={2} align="center" flexWrap="wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<ViewIcon />}
                              onClick={() =>
                                navigate(`/orders/${primaryOrder._id}`)
                              }
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleRow(row.key)}
                            >
                              {expanded ? "Hide details" : "More"}
                            </Button>
                          </HStack>
                        </Flex>

                        <SimpleGrid
                          columns={{ base: 1, lg: 3 }}
                          spacing={4}
                          mt={5}
                        >
                          <ActionPanel
                            title="1. Payment"
                            subtitle="Controls invoice and payment confirmation."
                            accent={paymentConfirmed ? "green" : "orange"}
                          >
                            <HStack spacing={2} mb={3} flexWrap="wrap">
                              <Badge
                                colorScheme={getPaymentStatusColorScheme(
                                  row.paymentStatus,
                                )}
                                px={3}
                                py={1.5}
                                borderRadius="full"
                                textTransform="capitalize"
                              >
                                {humanize(row.paymentStatus)}
                              </Badge>
                              <Badge
                                borderRadius="full"
                                px={3}
                                py={1.5}
                                colorScheme={
                                  paymentConfirmed ? "green" : "yellow"
                                }
                              >
                                {paymentConfirmed
                                  ? "Invoice paid"
                                  : "Invoice pending"}
                              </Badge>
                            </HStack>

                            <Text color={mutedText} fontSize="sm" mb={4}>
                              Use this only when money is confirmed. It does not
                              mean the parcel was shipped or delivered.
                            </Text>

                            <Stack
                              direction={{ base: "column", sm: "row" }}
                              spacing={2}
                            >
                              <Button
                                size="sm"
                                colorScheme={
                                  paymentConfirmed ? "green" : "blue"
                                }
                                isDisabled={paymentConfirmed || busy}
                                isLoading={
                                  actionBusyKey ===
                                  `${primaryOrder._id}:payment:paid`
                                }
                                onClick={() => markPaymentPaid(row)}
                              >
                                {paymentConfirmed ? "Paid" : "Mark Paid"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openInvoice(row)}
                              >
                                View Invoice
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => downloadInvoice(row)}
                              >
                                Download
                              </Button>
                            </Stack>
                          </ActionPanel>

                          <ActionPanel
                            title="2. Order Progress"
                            subtitle="Moves the customer fulfilment journey."
                            accent={getStatusColorScheme(progressStatus)}
                          >
                            <HStack spacing={2} mb={3} flexWrap="wrap">
                              <Badge
                                colorScheme={getStatusColorScheme(
                                  progressStatus,
                                )}
                                px={3}
                                py={1.5}
                                borderRadius="full"
                                textTransform="capitalize"
                              >
                                {humanize(progressStatus)}
                              </Badge>
                              <Badge borderRadius="full" px={3} py={1.5}>
                                {orderProgress.isException
                                  ? "Exception state"
                                  : orderProgress.current > 0
                                    ? `Step ${orderProgress.current}/${orderProgress.total}`
                                    : "Service status"}
                              </Badge>
                            </HStack>

                            <Text color={mutedText} fontSize="sm" mb={4}>
                              {humanize(row.serviceType)} fulfilment only.
                              Payment remains separate, and this list follows
                              the statuses allowed for this service and account
                              role.
                            </Text>

                            <Select
                              size="sm"
                              value=""
                              placeholder={
                                statusOptionsLoading
                                  ? "Loading service statuses..."
                                  : `Choose ${humanize(row.serviceType)} status`
                              }
                              onFocus={() =>
                                void loadOrderStatusOptions(primaryOrder)
                              }
                              onChange={(e) => {
                                const nextStatus = e.target.value;
                                if (!nextStatus) return;

                                void updateOrderProgress(
                                  row,
                                  primaryOrder._id,
                                  nextStatus,
                                  primaryOrder.userId,
                                );
                              }}
                              borderRadius="12px"
                              border={`1px solid ${theme.border}`}
                              bg={
                                isDark
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(255,255,255,0.95)"
                              }
                              color={textColor}
                              fontWeight="600"
                              isDisabled={
                                busy ||
                                statusOptionsLoading ||
                                availableStatusOptions.length === 0
                              }
                              _hover={{ borderColor: brand.electricBlue }}
                              _focus={{
                                borderColor: brand.electricBlue,
                                boxShadow: `0 0 0 1px ${brand.electricBlue}`,
                              }}
                              sx={selectOptionSx}
                            >
                              {availableStatusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </ActionPanel>

                          <ActionPanel
                            title="3. Dispatch & Driver"
                            subtitle="Assign drivers and open dispatch tools."
                            accent={getDispatchColorScheme(
                              row.dispatchStatus,
                              row.hasDeliveryWork,
                            )}
                          >
                            <HStack spacing={2} mb={3} flexWrap="wrap">
                              <Badge
                                colorScheme={getDispatchColorScheme(
                                  row.dispatchStatus,
                                  row.hasDeliveryWork,
                                )}
                                px={3}
                                py={1.5}
                                borderRadius="full"
                                textTransform="capitalize"
                              >
                                {row.hasDeliveryWork
                                  ? humanize(row.dispatchStatus)
                                  : "Not Required"}
                              </Badge>
                              {row.hasDeliveryWork && (
                                <Badge
                                  borderRadius="full"
                                  px={3}
                                  py={1.5}
                                  colorScheme={
                                    row.hasAssignedDriver ? "green" : "orange"
                                  }
                                >
                                  {row.hasAssignedDriver
                                    ? "Driver assigned"
                                    : "Driver needed"}
                                </Badge>
                              )}
                            </HStack>

                            <Text color={mutedText} fontSize="sm" mb={4}>
                              Use this to create/claim dispatch jobs and connect
                              a driver to the delivery.
                            </Text>

                            <Stack
                              direction={{ base: "column", sm: "row" }}
                              spacing={2}
                            >
                              {row.hasDeliveryWork &&
                                !row.hasAssignedDriver && (
                                  <Button
                                    size="sm"
                                    colorScheme="orange"
                                    onClick={() => openAssignDriverModal(row)}
                                  >
                                    Assign Driver
                                  </Button>
                                )}
                              {row.hasDeliveryWork && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openDispatchForRow(row)}
                                >
                                  Open Dispatch
                                </Button>
                              )}
                              {!row.hasDeliveryWork && (
                                <Button size="sm" variant="outline" isDisabled>
                                  No Delivery Work
                                </Button>
                              )}
                            </Stack>
                          </ActionPanel>
                        </SimpleGrid>
                      </Box>

                      <Collapse in={expanded} animateOpacity>
                        <Box px={{ base: 4, md: 5 }} pb={{ base: 4, md: 5 }}>
                          <Divider mb={4} />
                          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                            <DetailBlock
                              label="Checkout"
                              value={row.checkoutGroupId || row.key}
                              mutedValue={`${row.sellerCount} seller(s) • ${row.waybills.length} waybill(s)`}
                              onCopy={() =>
                                copy(
                                  row.checkoutGroupId || row.key,
                                  "Checkout ID",
                                )
                              }
                            />
                            <DetailBlock
                              label="Route"
                              value={
                                primaryOrder.fromAddress || "No pickup address"
                              }
                              mutedValue={
                                primaryOrder.toAddress || "No dropoff address"
                              }
                            />
                            <DetailBlock
                              label="Finance"
                              value={`Driver: ${formatMoney(primaryOrder.pricingBreakdown?.driverEarningTotal, currency)}`}
                              mutedValue={`Platform: ${formatMoney(primaryOrder.pricingBreakdown?.platformNetRevenue, currency)}`}
                            />
                            <DetailBlock
                              label="Dispatch IDs"
                              value={`Job: ${shortenId(primaryOrder.deliveryTracking?.dispatchJobId)}`}
                              mutedValue={`Driver: ${shortenId(primaryOrder.deliveryTracking?.assignedDriverId)}`}
                              onCopy={
                                primaryOrder.deliveryTracking?.dispatchJobId
                                  ? () =>
                                      copy(
                                        String(
                                          primaryOrder.deliveryTracking
                                            ?.dispatchJobId,
                                        ),
                                        "Dispatch Job ID",
                                      )
                                  : undefined
                              }
                            />
                          </SimpleGrid>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                {filteredRows.length === 0 && (
                  <Flex
                    py={10}
                    justify="center"
                    align="center"
                    direction="column"
                    gap={2}
                    borderRadius="20px"
                    bg={cardBg}
                    border={`1px solid ${theme.border}`}
                  >
                    <Text color={textColor} fontWeight="700">
                      No orders found
                    </Text>
                    <Text color={mutedText} fontSize="sm">
                      Try changing your search or filters.
                    </Text>
                  </Flex>
                )}
              </VStack>

              <AppPagination
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[5, 10, 20, 50]}
                showPageSizeSelector
                showSummary
              />
            </>
          )}
        </Box>
      </VStack>

      <Modal
        isOpen={Boolean(assignModalRow)}
        onClose={closeAssignDriverModal}
        size="3xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader color={textColor}>
            Assign Driver
            {assignModalRow && (
              <Text mt={1} fontSize="sm" color={mutedText} fontWeight="500">
                Order #{shortenId(assignModalRow.primaryOrder._id)} •{" "}
                {formatMoney(
                  assignModalRow.primaryOrder.pricingBreakdown
                    ?.driverEarningTotal,
                  assignModalRow.primaryOrder.currency || "MWK",
                )}{" "}
                driver earning
              </Text>
            )}
          </ModalHeader>

          <ModalCloseButton color={textColor} />

          <ModalBody>
            {assignModalRow && (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <DetailBlock
                    label="Pickup"
                    value={
                      assignModalRow.primaryOrder.fromAddress ||
                      "No pickup address"
                    }
                    mutedValue="Driver starts here"
                  />
                  <DetailBlock
                    label="Dropoff"
                    value={
                      assignModalRow.primaryOrder.toAddress ||
                      "No dropoff address"
                    }
                    mutedValue="Customer destination"
                  />
                </SimpleGrid>

                <Alert
                  status={assignModalRow.hasDispatchJob ? "info" : "warning"}
                  borderRadius="xl"
                >
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="700">
                      {assignModalRow.hasDispatchJob
                        ? "Existing dispatch job will be used."
                        : "A dispatch job will be created first, then assigned."}
                    </Text>
                    <Text fontSize="sm">
                      This keeps the delivery connected to driver tracking and
                      finance.
                    </Text>
                  </Box>
                </Alert>

                <Box>
                  <HStack justify="space-between" mb={2} align="center">
                    <Box>
                      <Text fontWeight="800" color={textColor}>
                        Select driver
                      </Text>
                      <Text fontSize="sm" color={mutedText}>
                        Recommended warehouse + nearby drivers appear first. You
                        can still search the full loaded driver list before
                        assigning.
                      </Text>
                    </Box>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void refreshAssignmentCandidates()}
                      isLoading={candidateRefreshLoading}
                    >
                      Refresh
                    </Button>
                  </HStack>

                  <Input
                    placeholder="Search driver by name, phone, vehicle or ID..."
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    mb={2}
                    borderRadius="14px"
                    borderColor={theme.border}
                    bg={theme.surface}
                    color={textColor}
                  />

                  <HStack spacing={2} mb={3} flexWrap="wrap">
                    <Badge
                      colorScheme="purple"
                      borderRadius="full"
                      px={2.5}
                      py={1}
                    >
                      {assignmentCandidateIds.size} recommended
                    </Badge>
                    <Badge
                      colorScheme="gray"
                      borderRadius="full"
                      px={2.5}
                      py={1}
                    >
                      {drivers.length} loaded drivers
                    </Badge>
                    {driverSearch.trim() && (
                      <Badge
                        colorScheme="blue"
                        borderRadius="full"
                        px={2.5}
                        py={1}
                      >
                        {filteredDrivers.length} matches
                      </Badge>
                    )}
                  </HStack>

                  <Box
                    border="1px solid"
                    borderColor={theme.border}
                    borderRadius="xl"
                    overflow="hidden"
                    maxH="360px"
                    overflowY="auto"
                  >
                    {filteredDrivers.length === 0 && (
                      <Box p={5}>
                        <Text color={mutedText} fontWeight="600">
                          {driverSearch.trim()
                            ? "No drivers match this search."
                            : "No drivers are loaded yet."}
                        </Text>
                        <Text color={subtleText} fontSize="sm" mt={1}>
                          Refresh the list, clear the search, or verify the
                          driver exists in Driver Ops.
                        </Text>
                      </Box>
                    )}

                    <RadioGroup
                      value={selectedDriverId}
                      onChange={setSelectedDriverId}
                    >
                      <Stack spacing={0}>
                        {filteredDrivers.map((driver) => {
                          const isActive =
                            normalizeStatusKey(driver.status) === "active";
                          const isOnShift =
                            normalizeStatusKey(driver.currentShiftStatus) ===
                            "on_shift";
                          const canAssign = isDriverEligibleForRow(
                            driver,
                            assignModalRow,
                          );
                          const currentJobs = Number(
                            driver.currentJobsCount || 0,
                          );
                          const maxJobs = Math.max(
                            1,
                            Number(driver.maxConcurrentJobs || 2),
                          );

                          return (
                            <Box
                              key={driver.id}
                              p={4}
                              borderBottom="1px solid"
                              borderColor={theme.border}
                              bg={
                                selectedDriverId === driver.id
                                  ? isDark
                                    ? "rgba(39,183,255,0.12)"
                                    : "rgba(39,183,255,0.08)"
                                  : theme.surface
                              }
                              opacity={canAssign ? 1 : 0.62}
                              _hover={{
                                bg: isDark
                                  ? "rgba(255,255,255,0.035)"
                                  : "rgba(39,183,255,0.04)",
                              }}
                            >
                              <Radio
                                value={driver.id}
                                isDisabled={!canAssign}
                                colorScheme="blue"
                              >
                                <HStack
                                  spacing={3}
                                  align="center"
                                  flexWrap="wrap"
                                >
                                  <Box>
                                    <Text color={textColor} fontWeight="800">
                                      {driver.name || "Unnamed Driver"}
                                    </Text>
                                    <Text color={mutedText} fontSize="sm">
                                      {driver.whatsappNumber ||
                                        driver.phoneNumber ||
                                        "No phone number"}{" "}
                                      {driver.vehicleType
                                        ? `• ${driver.vehicleType}`
                                        : ""}
                                    </Text>
                                  </Box>

                                  {(() => {
                                    const detail =
                                      assignmentCandidateDetails.get(
                                        String(driver.id),
                                      );
                                    const recommended =
                                      assignmentCandidateIds.has(
                                        String(driver.id),
                                      );
                                    const sourceLabel =
                                      detail?.candidateSource ===
                                      "warehouse_nearby"
                                        ? "Warehouse + Nearby"
                                        : detail?.candidateSource ===
                                            "warehouse"
                                          ? "Warehouse"
                                          : detail?.candidateSource === "nearby"
                                            ? "Nearby"
                                            : recommended
                                              ? "Recommended"
                                              : "Manual option";

                                    return (
                                      <>
                                        <Badge
                                          colorScheme={
                                            recommended ? "purple" : "gray"
                                          }
                                          borderRadius="full"
                                        >
                                          {sourceLabel}
                                        </Badge>
                                        {detail?.distanceToPickupKm != null && (
                                          <Badge
                                            colorScheme="green"
                                            borderRadius="full"
                                          >
                                            {Number(
                                              detail.distanceToPickupKm,
                                            ).toFixed(1)}{" "}
                                            km
                                            {detail.estimatedPickupMinutes !=
                                            null
                                              ? ` • ~${detail.estimatedPickupMinutes} min`
                                              : ""}
                                          </Badge>
                                        )}
                                      </>
                                    );
                                  })()}

                                  <Badge
                                    colorScheme={isActive ? "green" : "red"}
                                    borderRadius="full"
                                  >
                                    {driver.status || "unknown"}
                                  </Badge>
                                  <Badge
                                    colorScheme={isOnShift ? "cyan" : "gray"}
                                    borderRadius="full"
                                  >
                                    {driver.currentShiftStatus || "off_shift"}
                                  </Badge>
                                  <Badge
                                    borderRadius="full"
                                    colorScheme={
                                      currentJobs >= maxJobs ? "red" : "gray"
                                    }
                                  >
                                    {currentJobs}/{maxJobs} jobs
                                  </Badge>
                                  <Badge borderRadius="full" colorScheme="blue">
                                    {humanize(
                                      getDriverRequiredServiceCategory(
                                        assignModalRow,
                                      ),
                                    )}
                                  </Badge>
                                  {driver.ratingScore != null && (
                                    <Badge
                                      borderRadius="full"
                                      colorScheme="purple"
                                    >
                                      {Number(driver.ratingScore || 0).toFixed(
                                        1,
                                      )}{" "}
                                      rating
                                    </Badge>
                                  )}
                                </HStack>
                              </Radio>
                            </Box>
                          );
                        })}
                      </Stack>
                    </RadioGroup>
                  </Box>
                </Box>

                <Divider />

                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text color={mutedText} fontSize="sm">
                    Recommended candidates come from the warehouse + live nearby
                    search. Other loaded drivers remain searchable for manual
                    admin assignment, but unavailable drivers stay disabled.
                  </Text>

                  <HStack spacing={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openLiveDriverMapForAssignment}
                    >
                      Open live driver map
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        assignModalRow && openDispatchForRow(assignModalRow)
                      }
                    >
                      Open full dispatch page
                    </Button>
                  </HStack>
                </HStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={closeAssignDriverModal}
              isDisabled={assignLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={assignSelectedDriver}
              isLoading={assignLoading}
              isDisabled={!selectedDriverId}
            >
              Assign Driver
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function StatCard({
  label,
  value,
  tone = "normal",
}: {
  label: string;
  value: string | number;
  tone?: "normal" | "warning" | "success";
}) {
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const borderColor =
    tone === "warning"
      ? isDark
        ? "orange.700"
        : "orange.200"
      : tone === "success"
        ? isDark
          ? "green.700"
          : "green.200"
        : theme.border;

  const valueColor =
    tone === "warning"
      ? isDark
        ? "orange.200"
        : "orange.600"
      : tone === "success"
        ? isDark
          ? "green.200"
          : "green.600"
        : theme.text;

  return (
    <Stat
      p={4}
      borderRadius="18px"
      bg={theme.surface}
      border="1px solid"
      borderColor={borderColor}
      boxShadow={theme.shadowSm}
    >
      <StatLabel color={theme.textSubtle} fontWeight="700">
        {label}
      </StatLabel>
      <StatNumber color={valueColor} fontSize="xl">
        {value}
      </StatNumber>
    </Stat>
  );
}

function ActionPanel({
  title,
  subtitle,
  accent,
  children,
}: {
  title: string;
  subtitle: string;
  accent?: string;
  children: React.ReactNode;
}) {
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const borderColor =
    accent === "green"
      ? isDark
        ? "green.700"
        : "green.200"
      : accent === "orange" || accent === "yellow"
        ? isDark
          ? "orange.700"
          : "orange.200"
        : accent === "red"
          ? isDark
            ? "red.700"
            : "red.200"
          : theme.border;

  return (
    <Box
      p={4}
      borderRadius="18px"
      bg={theme.surface}
      border="1px solid"
      borderColor={borderColor}
      minH="220px"
    >
      <Text color={theme.text} fontWeight="900" mb={1}>
        {title}
      </Text>
      <Text color={theme.textMuted} fontSize="sm" mb={4}>
        {subtitle}
      </Text>
      {children}
    </Box>
  );
}

function MiniInfo({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper?: string;
}) {
  const { theme } = useCustomTheme();

  return (
    <Box
      p={3}
      borderRadius="16px"
      bg={theme.surfaceAlt}
      border="1px solid"
      borderColor={theme.border}
      minW={0}
    >
      <Text fontSize="xs" fontWeight="900" color={theme.textSubtle}>
        {label}
      </Text>
      <Tooltip label={value}>
        <Text fontWeight="800" color={theme.text} noOfLines={1}>
          {value}
        </Text>
      </Tooltip>
      {helper && (
        <Tooltip label={helper}>
          <Text color={theme.textMuted} fontSize="xs" noOfLines={1}>
            {helper}
          </Text>
        </Tooltip>
      )}
    </Box>
  );
}

function DetailBlock({
  label,
  value,
  mutedValue,
  onCopy,
}: {
  label: string;
  value: string;
  mutedValue?: string;
  onCopy?: () => void;
}) {
  const { theme } = useCustomTheme();

  return (
    <Box
      p={3}
      borderRadius="14px"
      bg={theme.surface}
      border="1px solid"
      borderColor={theme.border}
    >
      <HStack justify="space-between" align="start">
        <Box minW={0}>
          <Text fontSize="xs" fontWeight="800" color={theme.textSubtle}>
            {label}
          </Text>
          <Tooltip label={value}>
            <Text fontWeight="700" color={theme.text} noOfLines={1}>
              {value}
            </Text>
          </Tooltip>
          {mutedValue && (
            <Tooltip label={mutedValue}>
              <Text fontSize="xs" color={theme.textMuted} noOfLines={1}>
                {mutedValue}
              </Text>
            </Tooltip>
          )}
        </Box>

        {onCopy && (
          <IconButton
            aria-label={`Copy ${label}`}
            icon={<CopyIcon />}
            size="xs"
            variant="ghost"
            onClick={onCopy}
          />
        )}
      </HStack>
    </Box>
  );
}
